/**
 * The triage queue, as plain text for a terminal.
 *
 * ⛔ A RUN THAT FOUND DEFECTS IS A SUCCESSFUL RUN, AND NOTHING THIS MODULE EMITS MAY SAY OTHERWISE.
 * No finding is a failure, an error or a problem; the review worked, and this is what it produced.
 * verdict's worst documented defect was mapping a non-zero exit straight to "the harness
 * broke", which told the user the tool was broken at the exact moment it had done its job —
 * src/cli/index.ts carries the same ⛔ over the exit code, and this is its rendering half.
 *
 * ⛔ THE COMPONENT CHECKS ARE MARKS, NEVER A SCORE. `describeComponents` in src/engine/gate.ts is
 * the only producer of those lines and this module calls it rather than reformatting it, so the two
 * surfaces cannot say different things about the same finding. docs/spec/ui.md gives the reason a
 * number is refused: *"A person dismissing a finding should be able to see why the machine believed
 * it, and disagree with a specific component rather than with a score."* A competing tool was
 * abandoned after three pull requests for attaching confidence 4/5 to findings that were wrong.
 *
 * ⛔ EVERY COUNT CARRIES ITS DENOMINATOR. "7" is a number a reader has to go and contextualise;
 * "7 of 19" is a fact. The only bare number here is a total, which is its own denominator.
 *
 * ⛔ NOTHING IS OMITTED SILENTLY. If a caller filtered rows out, it says so through `excluded`, with
 * the count and the reason — the same rule src/config/schema.ts applies to a file skipped for being
 * too large ("excluded AND named in the report, never silently skipped") and src/engine/gate.ts
 * applies to a dropped finding.
 *
 * ⚠️ NO COLOUR UNLESS ASKED. Default off, so `sibyl triage | grep` and `sibyl triage > file` both
 * produce what is on the screen.
 */

import type { SibylConfig } from "../config/schema.js"
import { describeComponents } from "../engine/gate.js"
import type { TriageRecord } from "../triage/store.js"
import type { Components, Severity, TriageState } from "../types/finding.js"

export class QueueError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "QueueError"
  }
}

/** `triage.group_by` from the resolved config. Taken from the schema so the two cannot drift. */
export type QueueGrouping = SibylConfig["triage"]["groupBy"]

/** Something the caller removed from the list, named so the reader knows the list is partial. */
export interface QueueExclusion {
  /** What was hidden, as a plural noun phrase: "pre-existing findings". */
  readonly what: string
  readonly count: number
  /** Why, in a person's words. A filter with no stated reason is an unexplained gap. */
  readonly why: string
}

export interface RenderQueueOptions {
  readonly groupBy: QueueGrouping
  /**
   * The checks behind each finding, by fingerprint — normally this run's `Finding` objects.
   *
   * ⚠️ Optional because a `TriageRecord` deliberately does not carry them. The record is the durable
   * decision and survives a rebase; the components are evidence from one review of one sha. A
   * record with no entry here is SAID to have none, never rendered as six failed checks, which
   * would be a claim about the code that nothing measured.
   */
  readonly components?: ReadonlyMap<string, Components>
  readonly excluded?: readonly QueueExclusion[]
  /** Default false. ANSI is opt-in so the default output is pipeable. */
  readonly colour?: boolean
  readonly width?: number
}

const DEFAULT_WIDTH = 96

const SEVERITY_RANK: Record<Severity, number> = { high: 0, medium: 1, low: 2 }

const SEVERITY_COLOUR: Record<Severity, string> = {
  high: "\u001b[31m",
  medium: "\u001b[33m",
  low: "\u001b[90m",
}

const RESET = "\u001b[0m"

// ── Ordering ───────────────────────────────────────────────────────────────────────────────────

/**
 * Severity, then dimension, then path.
 *
 * ⚠️ Total, including the tie-breaks past the three the interface promises: line, then fingerprint.
 * Two findings on one path at one severity in one dimension would otherwise come out in whatever
 * order the store happened to hand them over, and a queue that reorders itself between two runs
 * over the same data cannot be read twice.
 */
export function compareForDisplay(a: TriageRecord, b: TriageRecord): number {
  return (
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] ||
    a.dimensionId.localeCompare(b.dimensionId) ||
    a.lastAnchor.path.localeCompare(b.lastAnchor.path) ||
    a.lastAnchor.line - b.lastAnchor.line ||
    a.fingerprint.localeCompare(b.fingerprint)
  )
}

/**
 * The group a record belongs to under `triage.group_by`.
 *
 * ⛔ The `default` arm throws. An exhaustive switch over a union has no runtime floor: a value from
 * a config file that was cast into `QueueGrouping` would fall through, return `undefined`, and
 * surface as every record landing in one nameless group — a queue that looks fine and is wrong.
 */
export function groupKeyOf(record: TriageRecord, groupBy: QueueGrouping): string {
  switch (groupBy) {
    case "file":
      return record.lastAnchor.path
    case "dimension":
      return record.dimensionId
    case "severity":
      return record.severity
    case "none":
      return ""
    default: {
      const unreachable: never = groupBy
      throw new QueueError(`${JSON.stringify(unreachable)} is not a triage.group_by value`)
    }
  }
}

// ── Rendering one record ───────────────────────────────────────────────────────────────────────

/** ⚠️ `LEFT` is the pre-image. Saying so is cheaper than a reader opening the wrong line. */
export function locationOf(record: TriageRecord): string {
  const { path, line, side } = record.lastAnchor
  return side === "LEFT" ? `${path}:${line} (pre-image)` : `${path}:${line}`
}

/**
 * What a person did about this finding, and who.
 *
 * ⛔ A dismissal names its reason from the fixed enum. "dismissed" with no reason is a decision
 * nobody can count, and dismissal rate by rule is the number this product is measured on.
 */
export function describeState(state: TriageState): string {
  switch (state.kind) {
    case "todo":
      return "todo"
    case "accepted":
      return `accepted by ${state.by}`
    case "dismissed":
      return state.note === undefined || state.note === ""
        ? `dismissed by ${state.by} — ${state.reason}`
        : `dismissed by ${state.by} — ${state.reason}: ${state.note}`
    default: {
      const unreachable: never = state
      throw new QueueError(`${JSON.stringify(unreachable)} is not a triage state`)
    }
  }
}

/** Enough of the fingerprint to name a finding to the CLI, with the rule still readable. */
export function shortFingerprint(fingerprint: string): string {
  const colon = fingerprint.indexOf(":")
  if (colon <= 0) return fingerprint.slice(0, 12)
  return `${fingerprint.slice(0, colon)}:${fingerprint.slice(colon + 1, colon + 9)}`
}

// ── Rendering the queue ────────────────────────────────────────────────────────────────────────

interface Group {
  readonly key: string
  readonly records: TriageRecord[]
}

function groupRecords(ordered: readonly TriageRecord[], groupBy: QueueGrouping): Group[] {
  const byKey = new Map<string, TriageRecord[]>()
  for (const record of ordered) {
    const key = groupKeyOf(record, groupBy)
    const bucket = byKey.get(key)
    if (bucket === undefined) byKey.set(key, [record])
    else bucket.push(record)
  }
  // ⭐ Insertion order is the order of the first record in each group, which is already severity
  // first — so the most serious file, dimension or level is at the top without a second sort.
  return [...byKey.entries()].map(([key, records]) => ({ key, records }))
}

function rule(text: string, width: number): string {
  const head = `── ${text} `
  return head + "─".repeat(Math.max(3, width - head.length))
}

function severityToken(severity: Severity, colour: boolean): string {
  const plain = severity.toUpperCase().padEnd(6)
  return colour ? `${SEVERITY_COLOUR[severity]}${plain}${RESET}` : plain
}

/**
 * The queue, as a person reads it.
 *
 * `records` is what the caller decided to show. It is never mutated and never filtered here: a
 * renderer that quietly drops rows is the defect this module's header forbids.
 */
export function renderQueue(records: readonly TriageRecord[], opts: RenderQueueOptions): string {
  const width = opts.width ?? DEFAULT_WIDTH
  const colour = opts.colour === true
  const excluded = opts.excluded ?? []
  const hidden = excluded.reduce((sum, e) => sum + e.count, 0)
  const shown = records.length
  const queued = shown + hidden
  const lines: string[] = []

  if (shown === 0) {
    lines.push(
      hidden === 0
        ? "Nothing is waiting in triage — 0 of 0 findings need a decision."
        : `Nothing is shown — 0 of ${queued} findings are in view.`,
    )
    lines.push(
      hidden === 0
        ? "Reviewed, found nothing. That is a result: the review ran, and it raised nothing."
        : "Every finding in the queue is hidden by a filter, not missing.",
    )
    appendExcluded(lines, excluded, shown, queued)
    return `${lines.join("\n")}\n`
  }

  const ordered = [...records].sort(compareForDisplay)
  const todo = ordered.filter((r) => r.state.kind === "todo").length
  const accepted = ordered.filter((r) => r.state.kind === "accepted").length
  const dismissed = ordered.filter((r) => r.state.kind === "dismissed").length
  const repeated = ordered.filter((r) => r.timesSeen > 1 && r.state.kind === "todo").length

  lines.push(
    `${shown} finding${shown === 1 ? "" : "s"} in triage — ${todo} of ${shown} waiting on a decision, ` +
      `${accepted} of ${shown} accepted, ${dismissed} of ${shown} dismissed.`,
  )
  lines.push(
    `Grouped by ${opts.groupBy}; ordered by severity, then dimension, then path. Nothing here has been posted.`,
  )
  if (repeated > 0) {
    // ⭐ src/triage/store.ts: "A finding seen five times and still todo is the stale one."
    lines.push(`${repeated} of ${shown} have been raised by more than one review and are still todo.`)
  }

  const dimensionWidth = Math.max(...ordered.map((r) => r.dimensionId.length), 9)
  const locationWidth = Math.max(...ordered.map((r) => locationOf(r).length), 12)

  for (const group of groupRecords(ordered, opts.groupBy)) {
    lines.push("")
    if (opts.groupBy !== "none") {
      lines.push(rule(`${group.key} — ${group.records.length} of ${shown}`, width))
    }
    for (const record of group.records) {
      lines.push(
        `  ${severityToken(record.severity, colour)}  ` +
          `${record.dimensionId.padEnd(dimensionWidth)}  ` +
          `${locationOf(record).padEnd(locationWidth)}  ` +
          describeState(record.state),
      )
      lines.push(`    ${record.title}`)
      lines.push(`    ${shortFingerprint(record.fingerprint)}`)
      const components = opts.components?.get(record.fingerprint)
      if (components === undefined) {
        // ⛔ Said, not shown as six ✗. The checks were not run against this sha; claiming they did
        // not hold would be a statement about the code that nothing measured.
        // ⚠️ "this review did not raise it again" was wrong here and measured wrong on a live queue:
        // a triage listing has no review in hand at all, so it said that about findings a review had
        // raised sixty seconds earlier. A durable record deliberately carries the DECISION, not the
        // components — those are evidence from one review of one sha — so the honest sentence names
        // where the checks live rather than making a claim about what some review did.
        lines.push("    the checks live with the run that raised it; a stored record keeps the decision, not the evidence")
      } else {
        for (const check of describeComponents(components)) lines.push(`    ${check}`)
      }
    }
  }

  appendExcluded(lines, excluded, shown, queued)
  return `${lines.join("\n")}\n`
}

function appendExcluded(
  lines: string[],
  excluded: readonly QueueExclusion[],
  shown: number,
  queued: number,
): void {
  if (excluded.length === 0) return
  lines.push("")
  lines.push("Not shown")
  for (const e of excluded) {
    lines.push(`  · ${e.count} of ${queued} ${e.what} — ${e.why}`)
  }
  lines.push(`  ${shown} of ${queued} in view.`)
}
